import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { generateSku, generateVariantSuffix, isAutoGeneratedSku } from '@/lib/sku';
import AiForgeButton from '@/components/admin/AiForgeButton';
import TemplateFieldsSection from '@/components/admin/TemplateFieldsSection';
import VariantFileUpload from '@/components/admin/VariantFileUpload';
import {
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  AlertCircle,
  Sparkles,
  Upload,
  Loader2,
  ExternalLink,
  RefreshCw,
  Send,
  X,
  Star,
  Pencil,
  Clock,
  Truck,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import TipTapEditor from '@/components/admin/TipTapEditor';
import Select from '@/components/ui/Select';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface ProductImage {
  id?: string;
  url: string;
  thumbnail_url?: string;
  alt_text?: string;
  display_order: number;
  is_primary: boolean;
}

interface UploadResponse {
  url: string;
  thumbnail_url: string;
  key: string;
  thumbnail_key: string;
  filename: string;
  original_size: number;
  optimized_size: number;
}

interface ProductFormData {
  name: string;
  slug: string;
  description: string;
  status: 'draft' | 'active' | 'archived';
  fulfillment_type: 'self' | 'dropship' | 'hybrid';
  is_featured: boolean;
  base_price: string;
  compare_at_price: string;
  cost_price: string;
  sku: string;
  images: ProductImage[];
  category_ids: string[];
  tags: string;
  plant_lifecycle: string;
  sun_requirement: string;
  water_requirement: string;
  difficulty_level: string;
  zone_min: string;
  zone_max: string;
  days_to_germination_min: string;
  days_to_germination_max: string;
  days_to_maturity_min: string;
  days_to_maturity_max: string;
  planting_depth: string;
  plant_spacing: string;
  row_spacing: string;
  height_min: string;
  height_max: string;
  planting_seasons: string[];
  harvest_seasons: string[];
  meta_title: string;
  meta_description: string;
  ai_text_model?: string;
  ai_image_model?: string;
  is_digital: boolean;
  download_limit: string;          // blank string = unlimited
  download_expiry_days: string;    // blank string = never expires
}

interface Variant {
  id?: string;
  name: string;
  price: string;
  sku: string;
  seed_count: string;
  weight_oz: string;
  stock: string;
}

interface Category {
  id: string;
  name: string;
  parent_id: string | null;
}

const INITIAL_FORM_DATA: ProductFormData = {
  name: '',
  slug: '',
  description: '',
  status: 'draft',
  fulfillment_type: 'self',
  is_featured: false,
  base_price: '',
  compare_at_price: '',
  cost_price: '',
  sku: '',
  images: [],
  category_ids: [],
  tags: '',
  plant_lifecycle: '',
  sun_requirement: '',
  water_requirement: '',
  difficulty_level: '',
  zone_min: '',
  zone_max: '',
  days_to_germination_min: '',
  days_to_germination_max: '',
  days_to_maturity_min: '',
  days_to_maturity_max: '',
  planting_depth: '',
  plant_spacing: '',
  row_spacing: '',
  height_min: '',
  height_max: '',
  planting_seasons: [],
  harvest_seasons: [],
  meta_title: '',
  meta_description: '',
  is_digital: false,
  download_limit: '',
  download_expiry_days: ''
};

const ProductEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<ProductFormData>(INITIAL_FORM_DATA);
  const [variants, setVariants] = useState<Variant[]>(
    isEditMode ? [] : [{ name: 'Standard Packet', price: '', sku: '', seed_count: '25', weight_oz: '', stock: '100' }]
  );
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState('');
  const regenInputRef = useRef<HTMLInputElement>(null);

  const [showAiStudio, setShowAiStudio] = useState(false);
  const [editingAltText, setEditingAltText] = useState<number | null>(null);

  // Audit trail & supplier info (populated on edit mode load)
  const [productMeta, setProductMeta] = useState<{
    created_at?: string;
    updated_at?: string;
    created_by_name?: string | null;
    updated_by_name?: string | null;
    suppliers?: Array<{
      id: string;
      name: string;
      website?: string;
      product_url?: string;
      supplier_sku?: string;
      cost_price?: string;
    }>;
  }>({});

  // Template state
  const [templateSlug, setTemplateSlug] = useState('seeds-plants');
  const [templateAttributes, setTemplateAttributes] = useState<Record<string, any>>({});
  const [availableTemplates, setAvailableTemplates] = useState<Array<{ id: string; name: string; slug: string; base_layout: string }>>([]);
  const isSeedTemplate = templateSlug === 'seeds-plants';

  // Dirty state tracking
  const savedStateRef = useRef<string>('');
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        const categoriesRes = await api.get<{ categories: Category[] }>('/admin/categories');
        setCategories(categoriesRes.categories);

        // Fetch available templates
        try {
          const templatesRes = await api.get<{ templates: any[] }>('/admin/templates');
          if (templatesRes.templates) {
            setAvailableTemplates(templatesRes.templates);
          }
        } catch {
          // Templates feature may not be available, ignore
        }

        if (isEditMode) {
          setIsLoading(true);
          const productRes = await api.get<any>(
            `/admin/products/${id}`
          );
          const { variants: productVariants, images: productImages, suppliers: productSuppliers, ...product } = productRes;

          setProductMeta({
            created_at: product.created_at,
            updated_at: product.updated_at,
            created_by_name: product.created_by_name,
            updated_by_name: product.updated_by_name,
            suppliers: productSuppliers || [],
          });

          setFormData({
            name: product.name || '',
            slug: product.slug || '',
            description: product.description || '',
            status: product.status || 'draft',
            fulfillment_type: product.fulfillment_type || 'self',
            is_featured: product.is_featured || false,
            base_price: product.base_price?.toString() || '',
            compare_at_price: product.compare_at_price?.toString() || '',
            cost_price: product.cost_price?.toString() || '',
            sku: product.sku || '',
            images: (productImages || []).map((img: any, idx: number) => ({
              id: img.id,
              url: img.url,
              thumbnail_url: img.thumbnail_url,
              alt_text: img.alt_text,
              display_order: img.display_order ?? idx,
              is_primary: img.is_primary || idx === 0,
            })),
            category_ids: (product.categories || []).map((c: any) => c.id),
            tags: (product.tags || []).map((t: any) => typeof t === 'string' ? t : t.name).join(', '),
            plant_lifecycle: product.plant_lifecycle || '',
            sun_requirement: product.sun_requirement || '',
            water_requirement: product.water_requirement || '',
            difficulty_level: product.difficulty_level || '',
            zone_min: product.zone_min?.toString() || '',
            zone_max: product.zone_max?.toString() || '',
            days_to_germination_min: product.days_to_germination_min?.toString() || '',
            days_to_germination_max: product.days_to_germination_max?.toString() || '',
            days_to_maturity_min: product.days_to_maturity_min?.toString() || '',
            days_to_maturity_max: product.days_to_maturity_max?.toString() || '',
            planting_depth: product.planting_depth || '',
            plant_spacing: product.plant_spacing || '',
            row_spacing: product.row_spacing || '',
            height_min: product.height_min?.toString() || '',
            height_max: product.height_max?.toString() || '',
            planting_seasons: product.planting_seasons || [],
            harvest_seasons: product.harvest_seasons || [],
            meta_title: product.meta_title || '',
            meta_description: product.meta_description || '',
            is_digital: product.is_digital === true,
            download_limit: product.download_limit != null ? String(product.download_limit) : '',
            download_expiry_days: product.download_expiry_days != null ? String(product.download_expiry_days) : ''
          });

          setVariants(
            productVariants.map((v: any) => {
              let weightOz = '';
              if (v.weight_amount != null) {
                const amt = parseFloat(v.weight_amount);
                if (v.weight_unit === 'lb' || v.weight_unit === 'lbs') weightOz = (amt * 16).toString();
                else if (v.weight_unit === 'g') weightOz = (amt / 28.3495).toFixed(1);
                else if (v.weight_unit === 'kg') weightOz = (amt * 35.274).toFixed(1);
                else weightOz = amt.toString(); // assume oz
              }
              return {
                id: v.id,
                name: v.name || '',
                price: v.price?.toString() || '',
                sku: v.sku || '',
                seed_count: v.seed_count?.toString() || '',
                weight_oz: weightOz,
                stock: (v.stock_quantity ?? v.stock)?.toString() || '',
              };
            })
          );

          // Load product template info
          if (product.template_id) {
            try {
              const templateRes = await api.get<any>(`/admin/templates/resolve/${id}`);
              if (templateRes.slug) {
                setTemplateSlug(templateRes.slug);
              }
              // Load template attribute data
              if (templateRes.base_layout && templateRes.base_layout !== 'seed_plant') {
                const attrRes = await api.get<any>(`/admin/attributes/products/${id}`);
                if (attrRes.attributes) {
                  const attrs: Record<string, any> = {};
                  for (const attr of attrRes.attributes) {
                    attrs[attr.slug.replace(/-/g, '_')] = attr.value_json || attr.value_text;
                  }
                  setTemplateAttributes(attrs);
                }
              }
            } catch {
              // Template resolution may fail if template doesn't exist, ignore
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  // Load available image models (provider selection handled by backend)
  // Snapshot saved state for dirty tracking (after initial load and after each save)
  useEffect(() => {
    if (!isLoading) {
      savedStateRef.current = JSON.stringify({ formData, variants });
    }
    // Only run when loading finishes, not on every change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const isDirty = useMemo(() => {
    if (!savedStateRef.current) return false;
    return JSON.stringify({ formData, variants }) !== savedStateRef.current;
  }, [formData, variants]);

  // Warn before leaving with unsaved changes (beforeunload only — useBlocker requires data router)
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Ctrl+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        const form = document.querySelector<HTMLFormElement>('#product-editor-form');
        form?.requestSubmit();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleInputChange = (field: keyof ProductFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (value: string) => {
    handleInputChange('name', value);
    if (!isEditMode || !formData.slug) {
      handleInputChange('slug', generateSlug(value));
    }
    const sku = generateSku(value);
    if (!isEditMode || !formData.sku) {
      handleInputChange('sku', sku);
    }
    // Auto-populate default variant SKU
    if (!isEditMode && variants.length > 0 && !variants[0].id && !variants[0].sku) {
      setVariants((prev) => {
        const updated = [...prev];
        updated[0] = { ...updated[0], sku: sku ? `${sku}-STD` : '' };
        return updated;
      });
    }
  };

  const handleVariantChange = (index: number, field: keyof Variant, value: string) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };

    // Auto-generate SKU when variant name changes
    if (field === 'name' && value.trim() && formData.sku) {
      const currentSku = newVariants[index].sku;
      if (!currentSku || isAutoGeneratedSku(currentSku, formData.sku)) {
        const otherSkus = variants.filter((_, i) => i !== index).map((v) => v.sku);
        const suffix = generateVariantSuffix(value, otherSkus);
        newVariants[index].sku = `${formData.sku}-${suffix}`;
      }
    }

    setVariants(newVariants);
  };

  const addVariant = () => {
    setVariants([
      ...variants,
      { name: '', price: formData.base_price, sku: '', seed_count: '', weight_oz: '', stock: '0' }
    ]);
  };

  const removeVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const newImages: ProductImage[] = [];

      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);

        const result = await api.upload<UploadResponse>('/admin/upload/image', fd);
        newImages.push({
          url: result.url,
          thumbnail_url: result.thumbnail_url,
          display_order: formData.images.length + newImages.length,
          is_primary: formData.images.length === 0 && newImages.length === 0,
        });
      }

      handleInputChange('images', [...formData.images, ...newImages]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const addImageByUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      const newImage: ProductImage = {
        url: url.trim(),
        display_order: formData.images.length,
        is_primary: formData.images.length === 0,
      };
      handleInputChange('images', [...formData.images, newImage]);
    }
  };

  const removeImage = (index: number) => {
    const image = formData.images[index];
    if (image.id) {
      setRemovedImageIds((prev) => [...prev, image.id!]);
    }
    const updated = formData.images.filter((_, i) => i !== index);
    // If removed image was primary, make the first remaining one primary
    if (image.is_primary && updated.length > 0) {
      updated[0] = { ...updated[0], is_primary: true };
    }
    handleInputChange('images', updated);
  };

  const setPrimaryImage = (index: number) => {
    const updated = formData.images.map((img, i) => ({
      ...img,
      is_primary: i === index,
    }));
    handleInputChange('images', updated);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    const updated = [...formData.images];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    // Update display_order to match new positions
    const reordered = updated.map((img, i) => ({ ...img, display_order: i }));
    handleInputChange('images', reordered);
  };

  const toggleSeason = (field: 'planting_seasons' | 'harvest_seasons', season: string) => {
    const current = formData[field];
    const updated = current.includes(season)
      ? current.filter((s) => s !== season)
      : [...current, season];
    handleInputChange(field, updated);
  };

  const handleGenerateImage = async (prompt: string) => {
    if (!prompt.trim()) return;
    try {
      const imageResult = await api.post<{ url: string; thumbnail_url?: string; modelUsed?: string }>(
        '/admin/ai/generate/product-image',
        { prompt: prompt.trim() }
      );
      if (imageResult.url) {
        setFormData((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            {
              url: imageResult.url,
              thumbnail_url: imageResult.thumbnail_url,
              display_order: prev.images.length,
              is_primary: prev.images.length === 0,
            },
          ],
          ai_image_model: imageResult.modelUsed || prev.ai_image_model,
        }));
        addToast('Image generated successfully', 'success');
      }
    } catch (imgErr: any) {
      console.warn('Image generation failed:', imgErr.message || 'Unknown error');
    }
  };

  const handleAiGenerate = async () => {
    if (!formData.name.trim() || isGenerating) return;
    try {
      setIsGenerating(true);
      setError(null);
      const res = await api.post<{ data?: any; modelUsed?: string; error?: string }>(
        '/admin/ai/generate-product',
        {
          product_name: formData.name.trim(),
          categories: categories.map((c) => ({ id: c.id, name: c.name })),
          template_slug: templateSlug,
        }
      );
      if (res.data) {
        const d = res.data;
        const aiTextModel = res.modelUsed || '';
        // Convert tags array to comma-separated string if needed
        const tagsValue = Array.isArray(d.tags) ? d.tags.join(', ') : (d.tags || '');

        // Update universal fields
        setFormData((prev) => ({
          ...prev,
          description: d.description || prev.description || '',
          tags: tagsValue || prev.tags || '',
          meta_title: d.meta_title || prev.meta_title || '',
          meta_description: d.meta_description || prev.meta_description || '',
          category_ids: Array.isArray(d.category_ids) && d.category_ids.length > 0 ? d.category_ids : prev.category_ids,
          ai_text_model: aiTextModel || prev.ai_text_model,
          // Seed-specific fields (only when seed template)
          ...(isSeedTemplate ? {
            plant_lifecycle: d.plant_lifecycle || prev.plant_lifecycle || '',
            sun_requirement: d.sun_requirement || prev.sun_requirement || '',
            water_requirement: d.water_requirement || prev.water_requirement || '',
            difficulty_level: d.difficulty_level || prev.difficulty_level || '',
            zone_min: d.zone_min != null ? String(d.zone_min) : prev.zone_min || '',
            zone_max: d.zone_max != null ? String(d.zone_max) : prev.zone_max || '',
            days_to_germination_min: d.days_to_germination_min != null ? String(d.days_to_germination_min) : prev.days_to_germination_min || '',
            days_to_germination_max: d.days_to_germination_max != null ? String(d.days_to_germination_max) : prev.days_to_germination_max || '',
            days_to_maturity_min: d.days_to_maturity_min != null ? String(d.days_to_maturity_min) : prev.days_to_maturity_min || '',
            days_to_maturity_max: d.days_to_maturity_max != null ? String(d.days_to_maturity_max) : prev.days_to_maturity_max || '',
            planting_depth: d.planting_depth || prev.planting_depth || '',
            plant_spacing: d.plant_spacing || prev.plant_spacing || '',
            row_spacing: d.row_spacing || prev.row_spacing || '',
            height_min: d.height_min != null ? String(d.height_min) : prev.height_min || '',
            height_max: d.height_max != null ? String(d.height_max) : prev.height_max || '',
            planting_seasons: d.planting_seasons?.length > 0 ? d.planting_seasons : prev.planting_seasons,
            harvest_seasons: d.harvest_seasons?.length > 0 ? d.harvest_seasons : prev.harvest_seasons,
          } : {}),
        }));

        // For non-seed templates, update template attributes from template_fields
        if (!isSeedTemplate && d.template_fields) {
          setTemplateAttributes((prev) => ({ ...prev, ...d.template_fields }));
        }

        // Auto-trigger image generation (non-blocking)
        if (d.image_prompt) {
          handleGenerateImage(d.image_prompt);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateDescription = async (instructions?: string) => {
    if (!formData.name.trim() || isRegenerating) return;
    try {
      setIsRegenerating(true);
      setError(null);
      const res = await api.post<{ description?: string; error?: string }>(
        '/admin/ai/regenerate-description',
        {
          product_name: formData.name.trim(),
          current_description: formData.description || undefined,
          instructions: instructions || undefined,
        }
      );
      if (res.description) {
        handleInputChange('description', res.description);
        setShowRegenInput(false);
        setRegenInstructions('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate description');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      await api.delete(`/admin/products/${id}?hard=true`);
      navigate('/admin/products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) errors.name = 'Product name is required';
    if (!formData.slug.trim()) errors.slug = 'Slug is required';
    if (!formData.base_price || parseFloat(formData.base_price) <= 0)
      errors.base_price = 'Valid base price is required';

    setValidationErrors(errors);

    // Scroll to first error field
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstErrorField = document.getElementById(`field-${errorKeys[0]}`);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = firstErrorField.querySelector('input, textarea, select');
        if (input instanceof HTMLElement) setTimeout(() => input.focus(), 400);
      }
      addToast('Please fill in the required fields', 'error');
    }

    return errorKeys.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const { images: _images, ...formDataWithoutImages } = formData;
      const payload = {
        ...formDataWithoutImages,
        base_price: parseFloat(formData.base_price),
        compare_at_price: formData.compare_at_price
          ? parseFloat(formData.compare_at_price)
          : null,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
        tags: formData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        zone_min: formData.zone_min ? parseInt(formData.zone_min) : null,
        zone_max: formData.zone_max ? parseInt(formData.zone_max) : null,
        days_to_germination_min: formData.days_to_germination_min
          ? parseInt(formData.days_to_germination_min)
          : null,
        days_to_germination_max: formData.days_to_germination_max
          ? parseInt(formData.days_to_germination_max)
          : null,
        days_to_maturity_min: formData.days_to_maturity_min
          ? parseInt(formData.days_to_maturity_min)
          : null,
        days_to_maturity_max: formData.days_to_maturity_max
          ? parseInt(formData.days_to_maturity_max)
          : null,
        height_min: formData.height_min ? parseFloat(formData.height_min) : null,
        height_max: formData.height_max ? parseFloat(formData.height_max) : null,
        is_digital: formData.is_digital,
        requires_shipping: !formData.is_digital,
        download_limit: formData.is_digital && formData.download_limit
          ? parseInt(formData.download_limit, 10)
          : null,
        download_expiry_days: formData.is_digital && formData.download_expiry_days
          ? parseInt(formData.download_expiry_days, 10)
          : null,
        variants: variants.map((v) => ({
          id: v.id,
          name: v.name,
          price: parseFloat(v.price),
          sku: v.sku,
          seed_count: v.seed_count ? parseInt(v.seed_count) : null,
          weight_amount: v.weight_oz ? parseFloat(v.weight_oz) : null,
          weight_unit: v.weight_oz ? 'oz' : null,
          stock: parseInt(v.stock)
        }))
      };

      let productId: string;

      if (isEditMode) {
        await api.put(`/admin/products/${id}`, payload);
        productId = id!;
      } else {
        const response = await api.post<{ id: string }>('/admin/products', payload);
        productId = response.id;
      }

      // Delete removed images
      for (const imageId of removedImageIds) {
        await api.delete(`/admin/products/${productId}/images/${imageId}`);
      }

      // Add new images (those without an id)
      const newImages = formData.images.filter((img) => !img.id);
      if (newImages.length > 0) {
        await api.post(`/admin/products/${productId}/images`, newImages);
      }

      // Snapshot saved state so isDirty resets
      savedStateRef.current = JSON.stringify({ formData, variants });
      addToast(isEditMode ? 'Product updated' : 'Product created', 'success');

      if (!isEditMode) {
        navigate(`/admin/products/${productId}`);
        return;
      }

      navigate('/admin/products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <form id="product-editor-form" onSubmit={handleSubmit}>
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/products">
              <Button variant="ghost" size="sm" type="button">
                <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditMode ? 'Edit Product' : 'New Product'}
              </h1>
              {isEditMode && (
                <p className="text-gray-600 mt-1">Update product details and variants</p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            {isEditMode && (
              <Button
                variant="danger"
                size="md"
                type="button"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="w-5 h-5 mr-2" aria-hidden="true" />
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              size="md"
              type="button"
              onClick={handleAiGenerate}
              isLoading={isGenerating}
              disabled={!formData.name.trim() || isGenerating}
            >
              <Sparkles className="w-5 h-5 mr-2" aria-hidden="true" />
              AI Generate
            </Button>
            <Link to="/admin/products">
              <Button variant="outline" size="md" type="button">
                Cancel
              </Button>
            </Link>
            <Button variant="primary" size="md" type="submit" isLoading={isSaving}>
              <Save className="w-5 h-5 mr-2" aria-hidden="true" />
              {isEditMode ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-medium text-red-800">Error saving product</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Primary Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Description */}
            <Card title="Product Information" padding="md">
              <div className="space-y-4">
                <div id="field-name">
                  <Input
                    label="Product Name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    error={validationErrors.name}
                    placeholder="e.g., Product Name"
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (showRegenInput) {
                          setShowRegenInput(false);
                          setRegenInstructions('');
                        } else if (formData.description) {
                          setShowRegenInput(true);
                          setTimeout(() => regenInputRef.current?.focus(), 0);
                        } else {
                          handleRegenerateDescription();
                        }
                      }}
                      disabled={!formData.name.trim() || isRegenerating}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={formData.description ? 'Regenerate description with AI' : 'Generate description with AI'}
                    >
                      {isRegenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                      ) : (
                        <RefreshCw className="w-3 h-3" aria-hidden="true" />
                      )}
                      {formData.description ? 'Regenerate' : 'Generate'}
                    </button>
                  </div>
                  {showRegenInput && (
                    <div className="mb-2 flex gap-2">
                      <input
                        ref={regenInputRef}
                        type="text"
                        value={regenInstructions}
                        onChange={(e) => setRegenInstructions(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleRegenerateDescription(regenInstructions);
                          }
                          if (e.key === 'Escape') {
                            setShowRegenInput(false);
                            setRegenInstructions('');
                          }
                        }}
                        placeholder="e.g., Make it shorter, add more details, emphasize key features..."
                        className="flex-1 rounded-lg border border-stone-300 px-3 py-1.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        disabled={isRegenerating}
                      />
                      <button
                        type="button"
                        onClick={() => handleRegenerateDescription(regenInstructions)}
                        disabled={isRegenerating}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isRegenerating ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="w-3.5 h-3.5" aria-hidden="true" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowRegenInput(false);
                          setRegenInstructions('');
                        }}
                        className="p-1.5 text-stone-400 hover:text-stone-600 rounded"
                        aria-label="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <TipTapEditor
                    content={formData.description}
                    onChange={(html) => handleInputChange('description', html)}
                  />
                </div>
              </div>
            </Card>

            {/* Media */}
            <Card title="Product Images" padding="md">
              <div className="space-y-4">
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {formData.images.map((img, index) => (
                      <div
                        key={img.id || `new-${index}`}
                        className={`relative group rounded-lg border-2 overflow-hidden ${
                          img.is_primary ? 'border-green-500 ring-2 ring-green-200' : 'border-stone-200'
                        }`}
                      >
                        <img
                          src={img.thumbnail_url || img.url}
                          alt={img.alt_text || `Product image ${index + 1}`}
                          className="w-full h-40 object-cover bg-stone-100"
                          onError={(e) => {
                            const target = e.currentTarget;
                            if (target.src !== img.url && img.thumbnail_url) {
                              target.src = img.url;
                            } else {
                              target.style.display = 'none';
                              target.parentElement!.classList.add('flex', 'items-center', 'justify-center');
                              const placeholder = document.createElement('div');
                              placeholder.className = 'flex flex-col items-center justify-center h-40 text-stone-400';
                              placeholder.innerHTML = '<svg class="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg><span class="text-xs">Image not found</span>';
                              target.parentElement!.appendChild(placeholder);
                            }
                          }}
                        />
                        {/* Always-visible delete button */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full shadow hover:bg-red-700 z-10"
                          aria-label={`Remove image ${index + 1}`}
                        >
                          <X className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                        {/* Hover overlay with actions */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 hover-reveal">
                          {!img.is_primary && (
                            <button
                              type="button"
                              onClick={() => setPrimaryImage(index)}
                              className="px-2 py-1 bg-white text-stone-800 text-xs font-medium rounded shadow hover:bg-stone-50"
                              title="Set as primary image"
                            >
                              <Star className="w-3 h-3 inline mr-1" aria-hidden="true" />
                              Primary
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setEditingAltText(editingAltText === index ? null : index)}
                            className="px-2 py-1 bg-white text-stone-800 text-xs font-medium rounded shadow hover:bg-stone-50"
                            title="Edit alt text"
                          >
                            <Pencil className="w-3 h-3 inline mr-1" aria-hidden="true" />
                            Alt
                          </button>
                        </div>
                        {/* Reorder arrows */}
                        <div className="absolute top-1.5 left-1.5 flex gap-1 hover-reveal">
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => moveImage(index, index - 1)}
                              className="p-1 bg-white/90 text-stone-700 rounded shadow text-xs hover:bg-white"
                              aria-label="Move image left"
                            >
                              &larr;
                            </button>
                          )}
                          {index < formData.images.length - 1 && (
                            <button
                              type="button"
                              onClick={() => moveImage(index, index + 1)}
                              className="p-1 bg-white/90 text-stone-700 rounded shadow text-xs hover:bg-white"
                              aria-label="Move image right"
                            >
                              &rarr;
                            </button>
                          )}
                        </div>
                        {/* Badges */}
                        <div className="absolute bottom-2 left-2 flex gap-1">
                          {img.is_primary && (
                            <div className="px-2 py-0.5 bg-green-600 text-white text-xs font-medium rounded">
                              Primary
                            </div>
                          )}
                        </div>
                        {/* Alt text editing */}
                        {editingAltText === index && (
                          <div className="absolute bottom-0 inset-x-0 bg-white/95 p-2 border-t border-stone-200">
                            <input
                              type="text"
                              value={img.alt_text || ''}
                              onChange={(e) => {
                                const updated = [...formData.images];
                                updated[index] = { ...updated[index], alt_text: e.target.value };
                                handleInputChange('images', updated);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Escape') setEditingAltText(null);
                              }}
                              placeholder="Image alt text..."
                              className="w-full rounded border border-stone-300 px-2 py-1 text-xs text-stone-900 focus:outline-none focus:ring-1 focus:ring-green-500"
                              autoFocus
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload zone */}
                <label
                  className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isUploading
                      ? 'border-green-300 bg-green-50'
                      : 'border-stone-300 hover:border-green-400 hover:bg-stone-50'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileUpload(e.dataTransfer.files);
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    disabled={isUploading}
                  />
                  {isUploading ? (
                    <>
                      <Loader2 className="w-10 h-10 mx-auto text-green-500 mb-3 animate-spin" aria-hidden="true" />
                      <p className="text-green-700 font-medium">Uploading and optimizing...</p>
                      <p className="text-sm text-green-600 mt-1">Converting to WebP format</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 mx-auto text-stone-400 mb-3" aria-hidden="true" />
                      <p className="text-stone-700 font-medium">Click or drag images here</p>
                      <p className="text-sm text-stone-500 mt-1">JPEG, PNG, WebP, GIF &middot; max 10MB &middot; auto-converted to WebP</p>
                    </>
                  )}
                </label>

                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" type="button" onClick={addImageByUrl}>
                    <ExternalLink className="w-4 h-4 mr-2" aria-hidden="true" />
                    Add Image by URL
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setShowAiStudio(true)}
                  >
                    <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" />
                    Generate with AI
                  </Button>
                </div>
              </div>
            </Card>

            {/* Pricing */}
            <Card title="Pricing" padding="md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div id="field-base_price"><Input
                  label="Base Price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.base_price}
                  onChange={(e) => {
                    handleInputChange('base_price', e.target.value);
                    // Sync to default variant price on new products
                    if (!isEditMode && variants.length > 0 && !variants[0].id && !variants[0].price) {
                      setVariants((prev) => {
                        const updated = [...prev];
                        updated[0] = { ...updated[0], price: e.target.value };
                        return updated;
                      });
                    }
                  }}
                  error={validationErrors.base_price}
                  placeholder="0.00"
                  required
                /></div>
                <Input
                  label="Compare at Price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.compare_at_price}
                  onChange={(e) => handleInputChange('compare_at_price', e.target.value)}
                  placeholder="0.00"
                  helperText="Original price for showing discounts"
                />
                <Input
                  label="Cost Price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_price}
                  onChange={(e) => handleInputChange('cost_price', e.target.value)}
                  placeholder="0.00"
                  helperText="Your cost (not shown to customers)"
                />
                <Input
                  label="SKU"
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value)}
                  placeholder="Auto-generated from name"
                  helperText="Auto-generated, or enter manually"
                />
              </div>
            </Card>

            {/* Product Template Selector */}
            <Card title="Product Type" padding="md">
              <Select
                label="Product Template"
                value={templateSlug}
                onChange={(e) => {
                  setTemplateSlug(e.target.value);
                  if (e.target.value !== 'seeds-plants') setTemplateAttributes({});
                }}
                options={
                  availableTemplates.length > 0
                    ? availableTemplates.map((t) => ({ label: t.name, value: t.slug }))
                    : [
                        { label: 'Seeds & Plants', value: 'seeds-plants' },
                        { label: 'Electronics', value: 'electronics' },
                        { label: 'Clothing & Apparel', value: 'clothing-apparel' },
                        { label: 'General / Custom', value: 'general' },
                      ]
                }
              />
            </Card>

            {/* Template-specific fields */}
            {!isSeedTemplate && (
              <TemplateFieldsSection
                templateSlug={templateSlug}
                values={templateAttributes}
                onChange={(key, value) => setTemplateAttributes((prev) => ({ ...prev, [key]: value }))}
              />
            )}

            {/* Growing Information (seed_plant only) */}
            {isSeedTemplate && <Card title="Growing Information" padding="md">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Plant Lifecycle"
                    value={formData.plant_lifecycle}
                    onChange={(e) => handleInputChange('plant_lifecycle', e.target.value)}
                    options={[
                      { label: 'Select lifecycle', value: '' },
                      { label: 'Annual', value: 'annual' },
                      { label: 'Perennial', value: 'perennial' },
                      { label: 'Biennial', value: 'biennial' }
                    ]}
                  />
                  <Select
                    label="Sun Requirement"
                    value={formData.sun_requirement}
                    onChange={(e) => handleInputChange('sun_requirement', e.target.value)}
                    options={[
                      { label: 'Select requirement', value: '' },
                      { label: 'Full Sun', value: 'full_sun' },
                      { label: 'Partial Sun', value: 'partial_sun' },
                      { label: 'Partial Shade', value: 'partial_shade' },
                      { label: 'Full Shade', value: 'full_shade' }
                    ]}
                  />
                  <Select
                    label="Water Requirement"
                    value={formData.water_requirement}
                    onChange={(e) => handleInputChange('water_requirement', e.target.value)}
                    options={[
                      { label: 'Select requirement', value: '' },
                      { label: 'Low', value: 'low' },
                      { label: 'Moderate', value: 'moderate' },
                      { label: 'High', value: 'high' }
                    ]}
                  />
                  <Select
                    label="Difficulty Level"
                    value={formData.difficulty_level}
                    onChange={(e) => handleInputChange('difficulty_level', e.target.value)}
                    options={[
                      { label: 'Select difficulty', value: '' },
                      { label: 'Beginner', value: 'beginner' },
                      { label: 'Intermediate', value: 'intermediate' },
                      { label: 'Advanced', value: 'advanced' }
                    ]}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input
                    label="Zone Min"
                    type="number"
                    min="1"
                    max="13"
                    value={formData.zone_min}
                    onChange={(e) => handleInputChange('zone_min', e.target.value)}
                    placeholder="3"
                  />
                  <Input
                    label="Zone Max"
                    type="number"
                    min="1"
                    max="13"
                    value={formData.zone_max}
                    onChange={(e) => handleInputChange('zone_max', e.target.value)}
                    placeholder="9"
                  />
                  <Input
                    label="Germ Days Min"
                    type="number"
                    min="0"
                    value={formData.days_to_germination_min}
                    onChange={(e) => handleInputChange('days_to_germination_min', e.target.value)}
                    placeholder="7"
                  />
                  <Input
                    label="Germ Days Max"
                    type="number"
                    min="0"
                    value={formData.days_to_germination_max}
                    onChange={(e) => handleInputChange('days_to_germination_max', e.target.value)}
                    placeholder="14"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input
                    label="Maturity Min"
                    type="number"
                    min="0"
                    value={formData.days_to_maturity_min}
                    onChange={(e) => handleInputChange('days_to_maturity_min', e.target.value)}
                    placeholder="60"
                    helperText="Days"
                  />
                  <Input
                    label="Maturity Max"
                    type="number"
                    min="0"
                    value={formData.days_to_maturity_max}
                    onChange={(e) => handleInputChange('days_to_maturity_max', e.target.value)}
                    placeholder="80"
                    helperText="Days"
                  />
                  <Input
                    label="Height Min"
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.height_min}
                    onChange={(e) => handleInputChange('height_min', e.target.value)}
                    placeholder="12"
                    helperText="Inches"
                  />
                  <Input
                    label="Height Max"
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.height_max}
                    onChange={(e) => handleInputChange('height_max', e.target.value)}
                    placeholder="24"
                    helperText="Inches"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Planting Depth"
                    value={formData.planting_depth}
                    onChange={(e) => handleInputChange('planting_depth', e.target.value)}
                    placeholder="1/4 inch"
                  />
                  <Input
                    label="Plant Spacing"
                    value={formData.plant_spacing}
                    onChange={(e) => handleInputChange('plant_spacing', e.target.value)}
                    placeholder="12 inches"
                  />
                  <Input
                    label="Row Spacing"
                    value={formData.row_spacing}
                    onChange={(e) => handleInputChange('row_spacing', e.target.value)}
                    placeholder="24 inches"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Planting Seasons
                    </label>
                    <div className="space-y-2">
                      {['Spring', 'Summer', 'Fall', 'Winter'].map((season) => (
                        <label key={season} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.planting_seasons.includes(season.toLowerCase())}
                            onChange={() =>
                              toggleSeason('planting_seasons', season.toLowerCase())
                            }
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{season}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Harvest Seasons
                    </label>
                    <div className="space-y-2">
                      {['Spring', 'Summer', 'Fall', 'Winter'].map((season) => (
                        <label key={season} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.harvest_seasons.includes(season.toLowerCase())}
                            onChange={() => toggleSeason('harvest_seasons', season.toLowerCase())}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{season}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>}

            {/* Digital Product Settings */}
            <Card title="Digital Product" padding="md">
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_digital}
                    onChange={(e) =>
                      setFormData({ ...formData, is_digital: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">This is a digital product</span>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Customers receive downloadable files instead of a physical shipment. No shipping address or
                      inventory tracking is required. Free ($0) digital products require the customer to be signed
                      in, which doubles as lead capture.
                    </p>
                  </div>
                </label>
                {formData.is_digital && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                    <Input
                      label="Download Limit"
                      type="number"
                      min="1"
                      value={formData.download_limit}
                      onChange={(e) =>
                        setFormData({ ...formData, download_limit: e.target.value })
                      }
                      placeholder="Unlimited"
                      helperText="Leave blank for unlimited downloads."
                    />
                    <Input
                      label="Expires After (days)"
                      type="number"
                      min="1"
                      value={formData.download_expiry_days}
                      onChange={(e) =>
                        setFormData({ ...formData, download_expiry_days: e.target.value })
                      }
                      placeholder="Never"
                      helperText="Leave blank for links that never expire."
                    />
                  </div>
                )}
              </div>
            </Card>

            {/* Variants */}
            <Card title="Product Variants" padding="md">
              <div className="space-y-4">
                {variants.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Price
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            SKU
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Seed Count
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Weight (oz)
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Stock
                          </th>
                          <th className="px-4 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {variants.map((variant, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={variant.name}
                                onChange={(e) =>
                                  handleVariantChange(index, 'name', e.target.value)
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., 50 seeds"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={variant.price}
                                onChange={(e) =>
                                  handleVariantChange(index, 'price', e.target.value)
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={variant.sku}
                                onChange={(e) =>
                                  handleVariantChange(index, 'sku', e.target.value)
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="SKU"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                min="0"
                                value={variant.seed_count}
                                onChange={(e) =>
                                  handleVariantChange(index, 'seed_count', e.target.value)
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="50"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={variant.weight_oz}
                                onChange={(e) =>
                                  handleVariantChange(index, 'weight_oz', e.target.value)
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="4.0"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                min="0"
                                value={variant.stock}
                                onChange={(e) =>
                                  handleVariantChange(index, 'stock', e.target.value)
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <button
                                type="button"
                                onClick={() => removeVariant(index)}
                                className="p-1 text-red-600 hover:text-red-800"
                                aria-label={`Remove variant ${variant.name || index + 1}`}
                              >
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    No variants added. Add variants to offer different package sizes or options.
                  </p>
                )}
                <Button variant="outline" size="sm" type="button" onClick={addVariant}>
                  <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                  Add Variant
                </Button>

                {formData.is_digital && (
                  <div className="mt-6 space-y-4 border-t border-stone-200 pt-4">
                    <h3 className="text-sm font-semibold text-stone-700">Downloadable Files</h3>
                    <p className="text-xs text-stone-500">
                      Attach files (PDF, XLSX, ZIP, etc.) to each variant. Customers will receive download
                      links via email after purchase and from their account page.
                    </p>
                    {variants.map((variant, idx) => (
                      <VariantFileUpload
                        key={variant.id || `new-${idx}`}
                        variantId={variant.id}
                        variantLabel={variant.name}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card title="Status" padding="md">
              <div className="space-y-4">
                <Select
                  label="Product Status"
                  value={formData.status}
                  onChange={(e) =>
                    handleInputChange('status', e.target.value as ProductFormData['status'])
                  }
                  options={[
                    { label: 'Draft', value: 'draft' },
                    { label: 'Active', value: 'active' },
                    { label: 'Archived', value: 'archived' }
                  ]}
                />
                <Select
                  label="Fulfillment"
                  value={formData.fulfillment_type}
                  onChange={(e) =>
                    handleInputChange('fulfillment_type', e.target.value as ProductFormData['fulfillment_type'])
                  }
                  options={[
                    { label: 'Self-fulfilled', value: 'self' },
                    { label: 'Dropshipped', value: 'dropship' },
                    { label: 'Hybrid', value: 'hybrid' },
                  ]}
                />
                <p className="text-xs text-gray-500 -mt-2">
                  {formData.fulfillment_type === 'self' && 'You handle storage and shipping'}
                  {formData.fulfillment_type === 'dropship' && 'Supplier ships directly to customer'}
                  {formData.fulfillment_type === 'hybrid' && 'Mix of self-fulfilled and dropship'}
                </p>
                {(formData.fulfillment_type === 'dropship' || formData.fulfillment_type === 'hybrid') && (
                  <Link to="/admin/suppliers" className="text-xs text-blue-600 hover:text-blue-800 -mt-1">
                    Manage Suppliers &rarr;
                  </Link>
                )}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Featured Product</span>
                </label>
              </div>
            </Card>

            {/* Activity / Audit Trail */}
            {isEditMode && (productMeta.created_at || productMeta.suppliers?.length) && (
              <Card title="Activity" padding="md">
                <div className="space-y-3 text-sm">
                  {productMeta.created_at && (
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-stone-600">
                          Created {new Date(productMeta.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        {productMeta.created_by_name && (
                          <p className="text-xs text-stone-400">by {productMeta.created_by_name}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {productMeta.updated_at && productMeta.updated_at !== productMeta.created_at && (
                    <div className="flex items-start gap-2">
                      <Pencil className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-stone-600">
                          Last edited {new Date(productMeta.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        {productMeta.updated_by_name && (
                          <p className="text-xs text-stone-400">by {productMeta.updated_by_name}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {productMeta.suppliers && productMeta.suppliers.length > 0 && (
                    <>
                      {productMeta.suppliers.map((supplier) => (
                        <div key={supplier.id} className="flex items-start gap-2">
                          <Truck className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
                          <div>
                            <Link
                              to="/admin/suppliers"
                              className="text-stone-700 hover:text-blue-600 font-medium"
                            >
                              {supplier.name}
                            </Link>
                            {supplier.supplier_sku && (
                              <p className="text-xs text-stone-400">SKU: {supplier.supplier_sku}</p>
                            )}
                            {supplier.product_url && (
                              <a
                                href={supplier.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                View vendor product <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* Categories */}
            <Card title="Categories" padding="md">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <label key={category.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.category_ids.includes(category.id)}
                        onChange={(e) => {
                          const newIds = e.target.checked
                            ? [...formData.category_ids, category.id]
                            : formData.category_ids.filter((id) => id !== category.id);
                          handleInputChange('category_ids', newIds);
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{category.name}</span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No categories available</p>
                )}
              </div>
            </Card>

            {/* Tags */}
            <Card title="Tags" padding="md">
              <Input
                label="Product Tags"
                value={formData.tags}
                onChange={(e) => handleInputChange('tags', e.target.value)}
                placeholder="tags, keywords, categories"
                helperText="Comma-separated tags"
              />
            </Card>

            {/* AI Settings */}
            <Card title="AI Settings" padding="md">
              <p className="text-xs text-stone-500">
                AI providers are configured in Settings &gt; AI Providers with automatic failover.
                Image model can be selected in the "Generate with AI" panel under Product Images.
              </p>
            </Card>

            {/* SEO */}
            <Card title="Search Engine Optimization" padding="md">
              <div className="space-y-4">
                <Input
                  label="Meta Title"
                  value={formData.meta_title}
                  onChange={(e) => handleInputChange('meta_title', e.target.value)}
                  placeholder={formData.name || 'Product name'}
                  helperText={`${formData.meta_title.length}/60 characters`}
                />
                <div id="field-slug"><Input
                  label="URL Slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  error={validationErrors.slug}
                  placeholder="product-slug"
                  required
                /></div>
                <Textarea
                  label="Meta Description"
                  value={formData.meta_description}
                  onChange={(e) => handleInputChange('meta_description', e.target.value)}
                  placeholder="Brief description for search engines..."
                  helperText={`${formData.meta_description.length}/160 characters`}
                  rows={3}
                />
              </div>
            </Card>

            {(formData.ai_text_model || formData.ai_image_model) && (
              <Card title="AI Attribution" padding="lg">
                <div className="space-y-1.5 text-xs text-gray-500">
                  {formData.ai_text_model && (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      <span>Text: <span className="font-mono text-gray-700">{formData.ai_text_model}</span></span>
                    </div>
                  )}
                  {formData.ai_image_model && (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      <span>Image: <span className="font-mono text-gray-700">{formData.ai_image_model}</span></span>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </form>

      {/* Floating save button */}
      {isDirty && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.2s_ease-out]">
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              const form = document.querySelector<HTMLFormElement>('#product-editor-form');
              form?.requestSubmit();
            }}
            isLoading={isSaving}
            className="shadow-lg shadow-green-500/20"
          >
            <Save className="w-5 h-5 mr-2" aria-hidden="true" />
            {isEditMode ? 'Save Changes' : 'Create Product'}
            <span className="ml-2 text-xs opacity-70">Ctrl+S</span>
          </Button>
        </div>
      )}

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Product"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{formData.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting}>
              Delete Product
            </Button>
          </div>
        </div>
      </Modal>

      <AiForgeButton
        isOpen={showAiStudio}
        onClose={() => setShowAiStudio(false)}
        context="product"
        contextName={formData.name}
        referenceImages={formData.images}
        onImageGenerated={(img) => {
          setFormData((prev) => ({
            ...prev,
            images: [
              ...prev.images,
              {
                url: img.url,
                thumbnail_url: img.thumbnail_url,
                alt_text: img.alt_text,
                display_order: prev.images.length,
                is_primary: prev.images.length === 0,
              },
            ],
          }));
          addToast('Image added to product', 'success');
        }}
      />
    </div>
  );
};

export default ProductEditor;
